import React, { FC } from 'react';
import moment from 'moment';
import { IABTest, IAbTests, IActiveABTest } from './types';
import { DEBUG_CONSOLE_MODE, logEvent } from '../../common/logs';

const AB_LS_KEY = 'ab-tests';
let _activeABTests: IActiveABTest = {};

interface AbTestProps {
    name: string;
    children?: React.ReactElement<ABTestVariantProps> | React.ReactElement<ABTestVariantProps>[];
    fallback: React.ReactNode;
}

export const ABTest = ({ name, fallback = null, children }: AbTestProps) => {
    const group = _activeABTests[name];
    let chosen;

    if (!children) {
        return fallback;
    }

    if (Array.isArray(children)) {
        chosen = children.find(child => child.props?.group === group);
    } else if (children.props?.group === group) {
        chosen = children;
    }

    if (!chosen) {
        return fallback;
    }

    return chosen;
};

interface ABTestVariantProps {
    group: string;
}

export const ABTestVariant: FC<ABTestVariantProps> = ({ children }) => <>{children}</>;

/**
 * Inicializuje hodnotu ab-testov pri spusteni stranky, a uz sa v case nemeni, nezmeni sa jeho hodnota, az kym neovbnovi stranku.
 *
 * @param initialAbTests
 *
 * Pokial je pouzivatel prihlaseny -> initialAbTests je pole (bool hodnota true).
 * Vtedy v local storage updateneme hodnotu, na novy stav ab-testov z pouzivatela.
 * Pouziju sa data o ab-testoch z pouzivatela.
 *
 * Pokial je pouzivatel odhlaseny -> initialAbTests ma hodnotu null/undefined.
 * Pouziju sa data o ab-testoch nacitane z local storage.
 *
 * V oboch pripadoch nasledne prebieha vypocet platnosti ab-testu, podla datumovej platnosti.
 */
export const initializeABTests = (initialAbTests?: IAbTests) => {
    let abTests;
    if (initialAbTests) {
        abTests = initialAbTests || [];
        setABtoLS(abTests);
    } else {
        abTests = getABfromLS();
    }

    _activeABTests = abTests.reduce((acc, abTest) => {
        if (!isABTestCurrentlyValid(abTest)) {
            return acc;
        }

        return Object.assign({}, acc, {
            [abTest.idFrontend]: abTest.group
        });
    }, {});

    if (DEBUG_CONSOLE_MODE) {
        logEvent('AB-TESTS', 'initialization', _activeABTests, 'purple');
    }

    return _activeABTests;
};

export const isABTestActive = (name: string) => !!_activeABTests[name];
export const isABTestGroupActive = (name: string, group: string) => _activeABTests[name] === group;

const getABfromLS = (): IAbTests => {
    try {
        return JSON.parse(window.localStorage.getItem(AB_LS_KEY)) || [];
    } catch (e) {
        console.error(e);
        setABtoLS([]);
        return [];
    }
};

const setABtoLS = (abTests: IAbTests = []) => window.localStorage.setItem(AB_LS_KEY, JSON.stringify(abTests));

const isABTestCurrentlyValid = (abTest: IABTest) =>
    moment().isBetween(dateFrom(abTest.validFrom), dateFrom(abTest.validTo));

const dateFrom = date => (typeof date === 'number' ? moment.unix(date) : moment(date));
